import { useMemo, useState } from "react";
import { tasksApi, ApiError } from "@/api";
import type { JobApplication, JobTask } from "@/types";
import { Button, Pill } from "@/components/ui";
import { taskPriorityTone, taskStatusTone, humanise } from "@/lib/jobs";
import { formatDateShort } from "@/lib/format";
import { DeadlineChipView } from "./JobChips";
import { TaskDrawer } from "./TaskDrawer";
import { TaskBoard } from "@/staff/tasks/TaskBoard";

type View = "list" | "board";

/** Tasks tab — full CRUD (brief §3.5). Sources the open tasks from the detail
 *  payload; every write re-fetches /detail via onChanged so deadlines/counts
 *  recompute rather than being reconciled locally. */
export function TasksTab({
  jobId,
  tasks,
  applications,
  onChanged,
  onError,
}: {
  jobId: number;
  tasks: JobTask[];
  applications: JobApplication[];
  onChanged: () => void;
  onError: (message: string) => void;
}) {
  // null = closed; "new" = create; a task = edit.
  const [drawer, setDrawer] = useState<JobTask | "new" | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [view, setView] = useState<View>("list");
  const [boardToken, setBoardToken] = useState(0);

  const appRefById = useMemo(() => {
    const map = new Map<number, string>();
    for (const a of applications) map.set(a.id, a.app_reference || `Draft #${a.id}`);
    return map;
  }, [applications]);

  async function runWrite(id: number, fn: () => Promise<unknown>) {
    setBusyId(id);
    try {
      await fn();
      onChanged();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not update the task.");
    } finally {
      setBusyId(null);
    }
  }

  function markDone(task: JobTask) {
    void runWrite(task.id, () => tasksApi.updateTask({ id: task.id, status: "done" }));
  }

  return (
    <div className="job-tasks">
      <div className="job-tasks__head">
        <p className="job-drawer__muted">{tasks.length} open task{tasks.length === 1 ? "" : "s"}</p>
        <div className="job-tasks__head-actions">
          <div className="tdash__pills" role="group" aria-label="View">
            <button type="button" className={`tdash-pill${view === "list" ? " is-on" : ""}`} onClick={() => setView("list")}>List</button>
            <button type="button" className={`tdash-pill${view === "board" ? " is-on" : ""}`} onClick={() => setView("board")}>Board</button>
          </div>
          <Button variant="primary" size="sm" onClick={() => setDrawer("new")}>New task</Button>
        </div>
      </div>

      {view === "board" ? (
        <TaskBoard
          jobId={jobId}
          reloadToken={boardToken}
          onMoved={onChanged}
          onOpenTask={(row) => {
            const jt = tasks.find((t) => t.id === row.id);
            if (jt) setDrawer(jt); // completed cards aren't in the open list → no-op
          }}
        />
      ) : tasks.length === 0 ? (
        <p className="jobs-list__empty">No open tasks. Add one to start tracking work.</p>
      ) : (
        <ul className="job-task-list">
          {tasks.map((task) => {
            const busy = busyId === task.id;
            const done = task.status === "done";
            const linkedRef = task.application_id ? appRefById.get(task.application_id) : null;
            return (
              <li key={task.id} className="job-task" onClick={() => setDrawer(task)} title="Edit task">
                {!done ? (
                  <button
                    type="button"
                    className="job-task__check"
                    disabled={busy}
                    aria-label="Mark done"
                    title="Mark done"
                    onClick={(e) => { e.stopPropagation(); markDone(task); }}
                  />
                ) : (
                  <span className="job-task__check job-task__check--done" aria-hidden="true">✓</span>
                )}
                <div className="job-task__main">
                  <div className="job-task__title-row">
                    <span className="job-task__title">{task.title}</span>
                    <Pill tone={taskStatusTone(task.status)}>{humanise(task.status)}</Pill>
                    <Pill tone={taskPriorityTone(task.priority)}>{humanise(task.priority)}</Pill>
                  </div>
                  <div className="job-task__meta">
                    {task.due_date || task.days_remaining != null ? (
                      <>
                        <span className="job-drawer__muted">Due {formatDateShort(task.due_date)}</span>
                        <DeadlineChipView daysRemaining={task.days_remaining} />
                      </>
                    ) : (
                      <span className="job-drawer__muted">No due date</span>
                    )}
                    {linkedRef ? <span className="job-task__link">↪ {linkedRef}</span> : null}
                  </div>
                  {task.notes ? <p className="job-task__notes">{task.notes}</p> : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {drawer ? (
        <TaskDrawer
          jobId={jobId}
          task={drawer === "new" ? null : drawer}
          applications={applications}
          onClose={() => setDrawer(null)}
          onSaved={() => {
            setDrawer(null);
            onChanged();
            setBoardToken((n) => n + 1);
          }}
        />
      ) : null}
    </div>
  );
}
